import React, { memo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Box, Popover, makeStyles } from '@material-ui/core';
import { COLOR_BORDER, COLOR_LIGHT_GRAY } from '../../styles/colors';
import { EvaluationFieldProps } from '../../types/Evaluation';

const useStyles = makeStyles(theme => ({
  colorSwatchWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 120,
  },
  inlineFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    width: '100%',
  },
  colorSwatchLabel: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.primary,
  },
  inlineSwatchValue: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.primary,
    marginBottom: 0,
    marginRight: theme.spacing(1),
  },
  singleSwatchRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'nowrap',
  },
  colorSwatch: {
    width: 30,
    height: 30,
    borderRadius: 5,
    backgroundColor: '#ee5a46',
    border: `1px solid ${COLOR_BORDER}`,
  },
  paletteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(32px, 1fr))',
    gap: theme.spacing(1),
    maxWidth: 260,
  },
  popoverContent: {
    padding: theme.spacing(1.5),
    maxWidth: 280,
  },
  selectedTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    border: 0,
    borderRadius: 10,
    padding: theme.spacing(1.25),
    backgroundColor: COLOR_LIGHT_GRAY,
    cursor: 'pointer',
    width: 'fit-content',
  },
  selectedValueText: {
    fontSize: theme.typography.pxToRem(13),
    marginRight: theme.spacing(1),
  },
  paletteChip: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${COLOR_BORDER}`,
    cursor: 'pointer',
    padding: 0,
    background: 'transparent',
  },
  paletteChipSelected: {
    boxShadow: '0 0 0 2px #111 inset',
  },
}), { name: 'ColorSwatch' });

function ColorSwatch({ name, control, label, readOnly, possibleValues, hideValue = false }: EvaluationFieldProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isReadOnly = readOnly === true || String(readOnly).toLowerCase() === 'true';
  const paletteColors = possibleValues ? Object.values(possibleValues).map(String) : [];
  const showPalette = !isReadOnly && paletteColors.length > 0;
  const popoverOpen = Boolean(anchorEl);

  function handleOpenPalette(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClosePalette() {
    setAnchorEl(null);
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Box className={classes.colorSwatchWrapper}>
          {hideValue ? (
            <div className={classes.inlineFieldRow}>
              <div className={classes.colorSwatchLabel}>{label}:</div>
              {showPalette ? (
                <button
                  type="button"
                  className={classes.selectedTrigger}
                  onClick={handleOpenPalette}
                  aria-label={`Open ${label} color palette`}
                >
                  <span className={classes.colorSwatch} style={{ backgroundColor: value || 'transparent' }} />
                </button>
              ) : (
                <div className={classes.colorSwatch} style={{ backgroundColor: value }} />
              )}
            </div>
          ) : (
            <>
              <div className={classes.colorSwatchLabel}>{label}</div>
              {showPalette ? (
                <button
                  type="button"
                  className={classes.selectedTrigger}
                  onClick={handleOpenPalette}
                  aria-label={`Open ${label} color palette`}
                >
                  <span className={classes.selectedValueText}>{value || 'Select color'}</span>
                  <span className={classes.colorSwatch} style={{ backgroundColor: value || 'transparent' }} />
                </button>
              ) : (
                <div className={classes.singleSwatchRow}>
                  <div className={classes.inlineSwatchValue}>{value}</div>
                  <div className={classes.colorSwatch} style={{ backgroundColor: value }} />
                </div>
              )}
            </>
          )}

          {showPalette && (
            <Popover
              open={popoverOpen}
              anchorEl={anchorEl}
              onClose={handleClosePalette}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box className={classes.popoverContent}>
                <div className={classes.paletteGrid}>
                  {paletteColors.map(color => {
                    const isSelected = String(value || '').toLowerCase() === color.toLowerCase();
                    return (
                      <button
                        key={color}
                        type="button"
                        className={`${classes.paletteChip} ${isSelected ? classes.paletteChipSelected : ''}`}
                        style={{ backgroundColor: color }}
                        title={color}
                        aria-label={`${label} ${color}`}
                        onClick={() => {
                          onChange(color);
                          handleClosePalette();
                        }}
                      />
                    );
                  })}
                </div>
              </Box>
            </Popover>
          )}
        </Box>
      )}
    />
  );
}

export default memo(ColorSwatch);
